import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import { MdsIcon } from "@sinch/components";
import { NotificationChannel } from "@sinch/components/application/Messages/useMessageChannels";
import { UserRoleEnum } from "@sinch/core/entities/serverEnums";
import type { useMuiForm } from "@sinch/hooks/useMuiForm";
import { MdsError } from "@sinch/icons";
import { isMobile } from "@sinch/utils/browser";
import { includes, intersection, isEmpty } from "ramda";
import React, { FC, type ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { EmailContent } from "./EmailContent";
import { SMSContent } from "./SMSContent";
import { RecipientsInput, RecipientsInputProps } from "./RecipientsInput";
import { PushContent } from "./PushContent";
import { MessageFormMode } from "./useMessageForm";

export type Placeholders = { label: ReactNode; value: string; size: number }[];

interface MessageFormProps {
  placeholders: Partial<Record<NotificationChannel, Placeholders>>;
  selectedChannel: NotificationChannel[];
  fillDefaultTemplate?: boolean;
  /**
   * Allowed channels for messaging.
   */
  allowedChannels: NotificationChannel[];
  mode?: MessageFormMode;
  attendanceIds?: string[];
  showRole?: boolean;
  /**
   * Restricts recipient search to users with one of the given base roles.
   */
  roles?: UserRoleEnum[];
  recipientsInputProps?: Partial<RecipientsInputProps>,
}

/**
 * Renders the message composition form: recipients input plus the content
 * editor for each selected channel (SMS, email, push) and any form-level errors.
 */
export const MessageForm: FC<MessageFormProps> = ({
  allowedChannels = [],
  mode = MessageFormMode.Default,
  attendanceIds,
  placeholders,
  selectedChannel,
  fillDefaultTemplate,
  showRole,
  roles,
  recipientsInputProps,
}) => {
  const { t } = useTranslation();
  const { getFormErrors } = useFormContext() as ReturnType<typeof useMuiForm>;

  const justSms = selectedChannel.length === 1 && selectedChannel.includes(NotificationChannel.Sms);

  const isReconfirmation = mode === MessageFormMode.Reconfirmation;
  const formErrors = getFormErrors() as string[];
  const isAllowed = intersection(selectedChannel, allowedChannels).length > 0;
  const isAllowedExcludeMobileSms =
    isAllowed || (selectedChannel.includes(NotificationChannel.Sms) && isMobile().isMobile);
  const emailContext = { attendance_id: attendanceIds };

  return (
    <Stack spacing={3}>
      <Box sx={{ opacity: isAllowedExcludeMobileSms ? 1 : 0.35 }}>
        <RecipientsInput
          {...recipientsInputProps}
          channelAddressProp={justSms ? "phone" : "email"}
          channels={selectedChannel}
          disabled={!isAllowedExcludeMobileSms}
          readOnly={isReconfirmation}
          roles={roles}
          showRole={showRole}
        />
      </Box>

      {selectedChannel.includes(NotificationChannel.Sms) && (
        <SMSContent
          allowed={includes(NotificationChannel.Sms, allowedChannels)}
          fillDefaultTemplate={fillDefaultTemplate}
          noNativeSend={isReconfirmation}
          placeholders={placeholders[NotificationChannel.Sms] ?? []}
        />
      )}

      {selectedChannel.includes(NotificationChannel.Email) && (
        <EmailContent
          context={emailContext}
          disableSubject={isReconfirmation}
          fillDefaultTemplate={fillDefaultTemplate}
          placeholders={placeholders[NotificationChannel.Email] ?? []}
        />
      )}

      {selectedChannel.includes(NotificationChannel.Push) && (
        <PushContent
          allowed={includes(NotificationChannel.Push, allowedChannels)}
          disableSubject={isReconfirmation}
          fillDefaultTemplate={fillDefaultTemplate}
          placeholders={placeholders[NotificationChannel.Push] ?? []}
        />
      )}

      {!isEmpty(formErrors) && (
        <Alert icon={<MdsIcon icon={MdsError} />} severity="error" sx={{ mb: 2 }}>
          <Stack>
            <AlertTitle sx={{ width: "100%" }}>{t("Error.somethingWentWrong")}</AlertTitle>
            {formErrors.map((message) => (
              <Typography key={message}>{message}</Typography>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
};
